const PDF_DATA_PREFIX = "data:application/pdf;base64,";

const PDF_DATA_PREFIX_PATTERN = /^data:application\/pdf;base64,/i;
const BASE64_PATTERN =
  /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

const hasControlCharacter = (value) => {
  return value.split("").some((character) => {
    const charCode = character.charCodeAt(0);

    return charCode < 32 || charCode === 127;
  });
};

const normalizePdfValue = (value) => {
  const normalizedValue = String(value || "").trim();

  if (hasControlCharacter(normalizedValue)) {
    return "";
  }

  return normalizedValue;
};

const isBase64Pdf = (value) => {
  return (
    value.length > 0 && value.length % 4 === 0 && BASE64_PATTERN.test(value)
  );
};

const getPdfDataPayload = (value) => {
  if (!PDF_DATA_PREFIX_PATTERN.test(value)) {
    return "";
  }

  return value.replace(PDF_DATA_PREFIX_PATTERN, "");
};

const isPdfDataUrl = (value) => {
  return isBase64Pdf(getPdfDataPayload(value));
};

const getUrlOrigin = (value) => {
  if (!value) {
    return "";
  }

  try {
    return new URL(value, window.location.origin).origin;
  } catch (error) {
    return "";
  }
};

const getAllowedPdfOrigins = () => {
  return [
    window.location.origin,
    getUrlOrigin(process.env.REACT_APP_API_URL),
    getUrlOrigin(process.env.REACT_APP_CLOUDFRONT_URL),
  ].filter(Boolean);
};

const isAllowedPdfOrigin = (parsedUrl) => {
  return getAllowedPdfOrigins().includes(parsedUrl.origin);
};

const isSafePdfUrl = (value) => {
  const isRelativeUrl = value.indexOf("/") === 0 && value.indexOf("//") !== 0;
  const hasExplicitScheme = /^[A-Za-z][A-Za-z0-9+.-]*:/.test(value);

  if (!isRelativeUrl && !hasExplicitScheme) {
    return false;
  }

  try {
    const parsedUrl = new URL(value, window.location.origin);
    return (
      ["blob:", "http:", "https:"].includes(parsedUrl.protocol) &&
      isAllowedPdfOrigin(parsedUrl)
    );
  } catch (error) {
    return false;
  }
};

export const getPdfUrl = (pdf) => {
  const value = normalizePdfValue(pdf);

  if (!value) {
    return "";
  }

  if (isPdfDataUrl(value) || isSafePdfUrl(value)) {
    return value;
  }

  if (!isBase64Pdf(value)) {
    return "";
  }

  return `${PDF_DATA_PREFIX}${value}`;
};

export const openPdfInNewTab = (pdf) => {
  const pdfSource = getPdfUrl(pdf);

  if (!pdfSource) {
    return;
  }

  if (isSafePdfUrl(pdfSource)) {
    window.open(pdfSource, "_blank", "noopener,noreferrer");
    return;
  }

  try {
    const pureBase64 = getPdfDataPayload(pdfSource);

    if (!pureBase64) {
      return;
    }

    const byteCharacters = window.atob(pureBase64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let index = 0; index < byteCharacters.length; index += 1) {
      byteNumbers[index] = byteCharacters.charCodeAt(index);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const fileUrl = window.URL.createObjectURL(blob);

    window.open(fileUrl, "_blank", "noopener,noreferrer");
    window.setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60000);
  } catch (error) {
    return;
  }
};
